import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Alert } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';

import { InteractionStatus } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { Button } from '@gtintel/signin/components/common/Button';
import { Input } from '@gtintel/signin/components/common/Input';
import { loginRequest } from '@gtintel/signin/config/authConfig';
import { useAuth } from '@gtintel/signin/hooks/useAuth';
import { authService } from '@gtintel/signin/services/auth.service';
import { LoginRequest } from '@gtintel/signin/types/auth.types';

const emailSchema = yup.object({
  email: yup.string().email('Invalid email').required('Email is required'),
});

const passwordSchema = yup.object({
  email: yup.string().email('Invalid email').required('Email is required'),
  password: yup.string().required('Password is required'),
  source_url: yup.string().optional(),
  remember_me: yup.boolean().required(),
});

interface UnifiedLoginFormProps {
  sourceUrl?: string;
}

export const UnifiedLoginForm: React.FC<UnifiedLoginFormProps> = ({ sourceUrl }) => {
  const { login, loading, error, clearError, setRedirectUrls, setUserEmail } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { instance, inProgress } = useMsal();

  const [userType, setUserType] = useState<'admin' | 'client' | 'partner' | null>(null);
  const [checkingUserType, setCheckingUserType] = useState(false);
  const [userTypeError, setUserTypeError] = useState<string | null>(null);
  const [currentEmail, setCurrentEmail] = useState<string>('');

  // Check if email is provided as URL parameter
  const emailParam = searchParams.get('email');

  const emailForm = useForm({
    resolver: yupResolver(emailSchema),
    defaultValues: {
      email: emailParam || '',
    },
  });

  const passwordForm = useForm<LoginRequest>({
    resolver: yupResolver(passwordSchema),
    defaultValues: {
      email: '',
      source_url: sourceUrl,
      remember_me: false,
    },
  });

  // Auto-check user type if email is provided as parameter
  useEffect(() => {
    if (emailParam) {
      handleEmailSubmit({ email: emailParam });
    }
  }, [emailParam]);

  useEffect(() => {
    return () => {
      clearError();
    };
  }, [clearError]);

  const handleEmailSubmit = async (data: { email: string }) => {
    setCheckingUserType(true);
    setUserTypeError(null);
    setCurrentEmail(data.email);

    try {
      const response = await authService.checkUserType(data.email);

      if (response.requires_azure_auth) {
        // Admin or partner users use Azure B2C
        setUserType(response.user_type as 'admin');

        // Store email for Azure B2C flow
        sessionStorage.setItem('admin_email', data.email);
        sessionStorage.setItem('admin_source_url', sourceUrl || '');

        // Don't trigger Azure login immediately - let useEffect handle it when MSAL is ready
      } else if (response.user_type === 'client') {
        setUserType('client');
        // Set email in password form
        passwordForm.setValue('email', data.email);
      } else {
        setUserTypeError('User not found. Please check your email address.');
      }
    } catch (err) {
      setUserTypeError('Failed to verify user type. Please try again.');
    } finally {
      setCheckingUserType(false);
    }
  };

  const handleAzureLogin = useCallback(async () => {
    if (inProgress !== InteractionStatus.None) {
      return;
    }

    if (!instance) {
      setUserTypeError('Authentication service not ready. Please try again.');
      return;
    }

    try {
      await instance.loginRedirect({
        ...loginRequest,
        loginHint: currentEmail, // Pass email as login hint to Azure B2C
      });
    } catch (err) {
      setUserTypeError(
        `Failed to initiate admin login: ${err instanceof Error ? err.message : 'Unknown error'}`
      );
    }
  }, [inProgress, instance, currentEmail]);

  // Handle Azure login when MSAL is ready and user type requires Azure auth
  useEffect(() => {
    if (
      (userType === 'admin' || userType === 'partner') &&
      inProgress === InteractionStatus.None &&
      instance &&
      currentEmail
    ) {
      handleAzureLogin();
    }
  }, [userType, inProgress, instance, currentEmail, handleAzureLogin]);

  const handlePasswordSubmit = async (data: LoginRequest) => {
    try {
      const response = await login(data);
      setUserEmail(data.email);

      if (response.mfa_required) {
        if (response.nexus_redirect_url && response.platform_redirect_url) {
          setRedirectUrls({
            nexus: response.nexus_redirect_url,
            platform: response.platform_redirect_url,
          });
        }

        navigate('/mfa/verify', {
          state: {
            temporaryToken: response.temporary_token,
            mfaMethods: response.mfa_methods,
            redirectUrl: response.redirect_url,
            sourceUrl: data.source_url,
            rememberMe: data.remember_me,
          },
        });
      } else if (response.success) {
        if (response.nexus_redirect_url && response.platform_redirect_url) {
          setRedirectUrls({
            nexus: response.nexus_redirect_url,
            platform: response.platform_redirect_url,
          });
        }

        if (!emailParam && !sourceUrl) {
          navigate('/apps');
        } else {
          window.location.href = response.redirect_url!;
        }
      }
    } catch (err) {
      // Error is already handled in the context
    }
  };

  const handleReset = () => {
    setUserType(null);
    setCurrentEmail('');
    setUserTypeError(null);
    emailForm.reset();
    passwordForm.reset();
    clearError();
  };

  // Show email form if no user type is determined yet
  if (!userType) {
    return (
      <form onSubmit={emailForm.handleSubmit(handleEmailSubmit)} className="space-y-4" noValidate>
        <div>
          <Input
            {...emailForm.register('email')}
            type="email"
            label="Email address"
            placeholder="Enter your email"
            error={emailForm.formState.errors.email?.message}
            autoComplete="email"
            autoFocus
            disabled={checkingUserType}
          />
        </div>

        {userTypeError && (
          <Alert variant="light" color="red" title="Error" icon={<IconAlertCircle />}>
            {userTypeError}
          </Alert>
        )}

        <Button type="submit" fullWidth loading={checkingUserType} size="lg">
          Continue
        </Button>
      </form>
    );
  }

  // Show password form for client users
  if (userType === 'client') {
    return (
      <form
        onSubmit={passwordForm.handleSubmit(handlePasswordSubmit)}
        className="space-y-4"
        noValidate
      >
        <div>
          <Input value={currentEmail} type="email" label="Email address" disabled readOnly />
          <button
            type="button"
            onClick={handleReset}
            className="mt-1 text-sm text-blue-600 hover:text-blue-500"
          >
            Use a different email
          </button>
        </div>

        <div>
          <Input
            {...passwordForm.register('password')}
            type="password"
            label="Password"
            placeholder="Enter your password"
            error={passwordForm.formState.errors.password?.message}
            autoComplete="current-password"
            autoFocus
            disabled={loading}
          />
        </div>

        <div className="flex items-center">
          <input
            {...passwordForm.register('remember_me')}
            id="remember_me"
            type="checkbox"
            className="h-4 w-4 text-gti-navy focus:ring-gti-navy border-gray-300 rounded"
          />
          <label htmlFor="remember_me" className="ml-2 block text-sm text-gti-grey font-montserrat">
            Remember me
          </label>
        </div>

        {error && (
          <Alert
            variant="light"
            color="red"
            title="Authentication failed"
            icon={<IconAlertCircle />}
          >
            {error}
          </Alert>
        )}

        <Button type="submit" fullWidth loading={loading} size="lg">
          Sign In
        </Button>
        <input type="hidden" {...passwordForm.register('source_url')} />
      </form>
    );
  }

  // Show loading state for admin/partner users (redirecting to Azure)
  return (
    <div className="text-center py-8">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto" />
      <p className="mt-4 text-gray-600">
        {userType === 'admin'
          ? 'Redirecting to admin authentication...'
          : 'Redirecting to partner authentication...'}
      </p>
      <p className="mt-2 text-sm text-gray-500">
        If this takes too long, try using a different email or refresh the page.
      </p>
      <div className="mt-4 space-x-4">
        <button
          type="button"
          onClick={handleReset}
          className="text-sm text-blue-600 hover:text-blue-500"
        >
          Use a different email
        </button>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="text-sm text-gray-600 hover:text-gray-500"
        >
          Refresh page
        </button>
      </div>
    </div>
  );
};
